import { useEffect, useState } from 'react';
import {
  Modal,
  Form,
  AutoComplete,
  Space,
  Typography,
  message,
  Button,
} from 'antd';
import { useDisclosure, useWindowSize } from '@/hooks';
import { useCustomerContacts } from '@/core/domain/people/CustomerContacts/CustomerContacts.hooks';
import flatContacts from '@/utils/functions/flatContacts';
import { removeMask, applyMask } from '@/utils/mask';
import { cellphoneMask } from '@/utils/mask/commonMasks';
import { ContactType } from '@/core/domain/_shared/ContactType';
import { FloatingFormItem, Group } from '@/components/_common';
import { useReportTokenGenerate } from '@/core/domain/reports/reportToken/reportToken.hooks';
import { ReportTokenType } from '@/core/domain/reports/reportToken/reportToken.types';
import WhatsAppIcon from '@/components/_common/WhatsAppIcon';
import { FaWhatsapp } from 'react-icons/fa';

interface Props {
  bankSlip: {
    id: number;
    printProtocol?: string;
    customerId?: number;
  };
  show?: boolean;
  type?: 'button' | 'icon';
}

export default function BankSlipWhatsApp({
  bankSlip,
  type = 'icon',
  show,
}: Props) {
  const reportTokenGenerate = useReportTokenGenerate();
  const { isMobile } = useWindowSize();
  const [opened, action] = useDisclosure();
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm<{ number?: string }>();

  const phoneNumber = Form.useWatch(['number'], form);
  const isValidPhoneNumber = !!phoneNumber && (phoneNumber?.length || 0) >= 10;

  const customerContacts = useCustomerContacts(
    bankSlip.customerId,
    opened && !!bankSlip.customerId,
  );

  const contacts = flatContacts(customerContacts.data).filter(
    (contact) =>
      contact.contactType === ContactType.Phone ||
      contact.contactType === ContactType.Mobile ||
      contact.contactType === ContactType.WhatsApp,
  );

  const handleClose = () => {
    form.resetFields();
    action.close();
  };

  async function sendMessage(number: string) {
    setLoading(true);

    try {
      const tokenData = await reportTokenGenerate.mutateAsync({
        params: JSON.stringify({ bankSlipId: bankSlip.id }),
        reportType: ReportTokenType.BANK_SLIP,
      });

      if (!tokenData.token) {
        return message.warn('Não foi possível gerar o link do boleto.');
      }

      const bankSlipUrl = `${window.location.origin}/r/bank-slip?token=${tokenData.token}`;
      const text = `Olá! Segue o link para acessar seu boleto:\n\n${bankSlipUrl}`;
      const wppNumber = `55${number}`;

      if (isMobile) {
        window.open(
          `https://wa.me/${wppNumber}?text=${encodeURIComponent(text)}`,
          '_blank',
        );
      } else {
        const params = new URLSearchParams({ phone: wppNumber, text });
        window.open(`https://web.whatsapp.com/send?${params}`, '_blank');
      }
    } finally {
      setLoading(false);
    }
  }

  const onSubmit = async () => {
    const values = await form.validateFields();
    const number = removeMask(values.number);

    await sendMessage(number);
    handleClose();
  };

  const disabled = !bankSlip.printProtocol;

  useEffect(() => {
    if (opened && !!contacts.length) {
      form.setFieldValue(
        'number',
        applyMask(removeMask(contacts?.at(0)?.value), cellphoneMask),
      );
    }
  }, [opened, customerContacts.dataUpdatedAt]);

  if (!show) return null;

  return (
    <>
      {type === 'icon' && (
        <WhatsAppIcon
          disabled={disabled}
          content={
            disabled
              ? 'Consulte o PDF do boleto antes de enviar pelo WhatsApp'
              : 'Enviar boleto via WhatsApp'
          }
          iconProps={{ onClick: action.open }}
          popoverProps={{ placement: disabled ? 'left' : undefined }}
        />
      )}

      {type === 'button' && (
        <Button type="primary" disabled={disabled} onClick={action.open}>
          <Group gap={5}>
            <FaWhatsapp size={18} />
            Enviar boleto
          </Group>
        </Button>
      )}

      <Modal
        open={opened}
        onCancel={handleClose}
        confirmLoading={loading}
        onOk={onSubmit}
        okText="Enviar boleto"
        title="Enviar boleto por WhatsApp"
        okButtonProps={{ disabled: !isValidPhoneNumber }}
      >
        <Form form={form} preserve={false}>
          <Space direction="vertical">
            <Typography.Text>
              Preencha o número do WhatsApp do cliente para enviar o link do
              boleto.
            </Typography.Text>

            <FloatingFormItem
              name="number"
              label="Número do cliente"
              getValueFromEvent={(value) => {
                if (value.length < 11) return removeMask(value);
                return applyMask(removeMask(value), cellphoneMask);
              }}
            >
              <AutoComplete
                getPopupContainer={(node) => node.parentNode}
                options={contacts.map((contact) => ({
                  value: contact.value,
                  label: applyMask(removeMask(contact.value), cellphoneMask),
                }))}
              />
            </FloatingFormItem>
          </Space>
        </Form>
      </Modal>
    </>
  );
}
